import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Wand2, Search, RefreshCw, Linkedin, ShieldQuestion, Loader2, ShieldCheck, Copy, ClipboardList, UserPlus } from "lucide-react";

const TARGET_TITLES = "CEO, President, Founder, Owner, COO, Director of Operations, Contracts Manager, Proposal Manager, Compliance Manager, FSO, IT Manager, Director of IT, CIO, CTO, ISSM, Security Manager";

const CONF_STYLES = {
  High: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Medium: "bg-amber-100 text-amber-800 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};

function ManualField({ label, v = "", on, placeholder = "", type = "text", required = false, disabled = false, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input
        type={type}
        value={v}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => on && on(e.target.value)}
        data-testid={testid}
        className="text-sm"
      />
    </div>
  );
}

// A contact is "verified" when it has a High-confidence, non-predicted email or a phone number.
function isVerified(c) {
  const goodEmail = !!c.email && !c.email_predicted && c.confidence === "High";
  const hasPhone = !!c.phone;
  return goodEmail || hasPhone;
}

class DMErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error("FindDecisionMaker crashed:", err); }
  render() {
    if (this.state.hasError) {
      return (
        <Dialog open={this.props.open} onOpenChange={this.props.onOpenChange}>
          <DialogContent className="max-w-lg" data-testid="dm-error-boundary">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-navy">Decision Maker</DialogTitle>
            </DialogHeader>
            <div className="rounded-md border-l-4 border-l-red-400 bg-red-50 border border-red-200 p-4 my-2">
              <p className="text-sm text-red-800 font-medium">Apollo live enrichment is unavailable. Use Manual Apollo Assist.</p>
              <p className="text-xs text-red-700 mt-1">Something went wrong rendering results. Please close and try again.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => this.props.onOpenChange(false)} data-testid="dm-boundary-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    }
    return this.props.children;
  }
}

export function FindDecisionMaker(props) {
  return (
    <DMErrorBoundary open={props.open} onOpenChange={props.onOpenChange}>
      <FindDecisionMakerInner {...props} />
    </DMErrorBoundary>
  );
}

function FindDecisionMakerInner({ open, onOpenChange, lead = {}, hasExistingContact, onApply, onSaveAdditional }) {
  const [state, setState] = useState({ status: "idle" });
  const [selected, setSelected] = useState(null);
  const [pendingApply, setPendingApply] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [manual, setManual] = useState({ first_name: "", last_name: "", title: "", email: "", phone: "", linkedin_url: "", source: "Manual (Apollo)", confidence: "Medium" });

  const fallback = ["error", "no_results", "no_identifier", "not_configured"].includes(state.status);
  const apolloQuery = [lead.company_name, lead.website].filter(Boolean).join(" ").trim();

  const copyQuery = () => { navigator.clipboard.writeText(apolloQuery || lead.company_name || ""); toast.success("Apollo search query copied"); };

  const saveManual = (asAdditional) => {
    if (!manual.first_name && !manual.last_name && !manual.email) { toast.error("Enter at least a name or email"); return; }
    const note = [
      `Manual Apollo Assist entry on ${new Date().toLocaleString()}`,
      `Company/identifier: ${apolloQuery || lead.company_name || "n/a"}`,
      `Contact: ${manual.first_name} ${manual.last_name}${manual.title ? ` (${manual.title})` : ""}`,
      `Source: ${manual.source} · Confidence: ${manual.confidence}`,
    ].join("\n");
    (asAdditional ? onSaveAdditional : onApply)(manual, note);
    onOpenChange(false);
    toast.success(asAdditional ? "Saved as additional contact" : "Saved as primary contact");
  };

  const search = async () => {
    setState({ status: "loading" });
    setSelected(null);
    try {
      const res = await api.post("/enrichment/find-decision-maker", {
        company_name: lead.company_name || "",
        website: lead.website || "",
        cage_code: lead.cage_code || "",
        phone: lead.phone || "",
        email: lead.email || "",
      });
      setState(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Search failed";
      setState({ status: "error", message: detail });
      toast.error(detail);
    }
  };

  // Auto-search when opened
  React.useEffect(() => {
    if (open) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buildNote = (c) => {
    const parts = [
      `Decision-maker search on ${new Date().toLocaleString()}`,
      `Identifier used: ${state.identifier_used || "n/a"}`,
      `Selected: ${c.first_name} ${c.last_name}${c.title ? ` (${c.title})` : ""}`,
      `Source: ${c.source}`,
      `Confidence: ${c.confidence}${c.email_predicted ? " · email predicted (unverified)" : ""}`,
    ];
    return parts.join("\n");
  };

  const doApply = (c) => {
    onApply(c, buildNote(c));
    onOpenChange(false);
    toast.success("Contact applied to lead");
  };

  const handleApply = () => {
    if (!selected) return;
    if (hasExistingContact) { setPendingApply(true); return; }
    doApply(selected);
  };

  const handleSaveAdditional = () => {
    if (!selected) return;
    onSaveAdditional(selected, buildNote(selected));
    onOpenChange(false);
    toast.success("Saved as additional contact");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="decision-maker-results">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-gold-dark" aria-hidden="true" /> Decision Maker Results
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 min-h-[200px]">
          {state.status === "loading" && (
            <div className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground" data-testid="dm-loading">
              <Loader2 className="h-6 w-6 animate-spin text-gold-dark" />
              <p className="text-sm">Searching enrichment provider for likely decision-makers…</p>
            </div>
          )}

          {state.status === "not_configured" && (
            <div className="rounded-md border-l-4 border-l-gold bg-slate-50 border border-border p-5" data-testid="dm-not-configured">
              <div className="flex items-center gap-2 text-navy font-medium mb-2">
                <ShieldQuestion className="h-4 w-4 text-gold-dark" /> Live enrichment not connected
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{state.message}</p>
            </div>
          )}

          {state.status === "error" && (
            <div className="rounded-md border-l-4 border-l-red-400 bg-red-50 border border-red-200 p-4" data-testid="dm-error">
              <p className="text-sm text-red-800 font-medium mb-1">Apollo live enrichment is unavailable. Use Manual Apollo Assist.</p>
              <p className="text-xs text-red-700 leading-relaxed">{state.message}{state.error_category ? ` (${state.error_category})` : ""}</p>
            </div>
          )}

          {(state.status === "no_results" || state.status === "no_identifier") && (
            <div className="text-center py-6 text-muted-foreground text-sm" data-testid="dm-empty">{state.message}</div>
          )}

          {fallback && (
            <div className="mt-4 rounded-md border border-border p-4" data-testid="manual-apollo-assist">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                <h4 className="font-display font-bold text-navy text-sm">Manual Apollo Assist</h4>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mb-3 text-xs">
                <div><span className="text-muted-foreground">Company</span><p className="text-navy font-medium">{lead.company_name || "—"}</p></div>
                <div><span className="text-muted-foreground">Website / domain</span><p className="text-navy font-medium">{lead.website || "—"}</p></div>
                <div><span className="text-muted-foreground">CAGE code</span><p className="text-navy font-medium font-mono">{lead.cage_code || "—"}</p></div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Input readOnly value={apolloQuery || lead.company_name || ""} className="text-xs font-mono" data-testid="apollo-query" />
                <Button size="sm" variant="outline" onClick={copyQuery} data-testid="copy-apollo-query"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
              </div>
              <div className="rounded bg-slate-50 border border-border p-2 mb-4">
                <p className="text-[11px] text-muted-foreground"><span className="font-semibold text-navy">Suggested title filters:</span> {TARGET_TITLES}</p>
              </div>

              <p className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold mb-2">Enter contact found in Apollo</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <ManualField label="First name" v={manual.first_name} on={(x) => setManual((m) => ({ ...m, first_name: x }))} testid="manual-first" />
                <ManualField label="Last name" v={manual.last_name} on={(x) => setManual((m) => ({ ...m, last_name: x }))} testid="manual-last" />
                <ManualField label="Title" v={manual.title} on={(x) => setManual((m) => ({ ...m, title: x }))} />
                <ManualField label="Email" v={manual.email} on={(x) => setManual((m) => ({ ...m, email: x }))} testid="manual-email" />
                <ManualField label="Phone" v={manual.phone} on={(x) => setManual((m) => ({ ...m, phone: x }))} />
                <ManualField label="LinkedIn URL" v={manual.linkedin_url} on={(x) => setManual((m) => ({ ...m, linkedin_url: x }))} />
                <ManualField label="Source" v={manual.source} on={(x) => setManual((m) => ({ ...m, source: x }))} />
                <div className="space-y-1.5">
                  <Label className="text-xs">Confidence</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={manual.confidence} onChange={(e) => setManual((m) => ({ ...m, confidence: e.target.value }))}>
                    {["High", "Medium", "Low"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" onClick={() => saveManual(false)} data-testid="manual-save-primary" className="bg-navy text-white hover:bg-navy-700">
                  <UserPlus className="h-3.5 w-3.5 mr-1" /> Save as Primary Contact
                </Button>
                <Button size="sm" variant="outline" onClick={() => saveManual(true)} data-testid="manual-save-additional">Save as Additional Contact</Button>
                <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {state.status === "ok" && (() => {
            const shown = verifiedOnly ? state.results.filter(isVerified) : state.results;
            const hiddenCount = state.results.length - shown.length;
            return (
            <>
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-xs text-muted-foreground">
                  Ranked by likely buying authority · identifier: <span className="font-mono">{state.identifier_used}</span>.
                  Review before outreach.
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <ShieldCheck className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                  <Label htmlFor="verified-only" className="text-xs font-medium text-navy cursor-pointer">Verified Only</Label>
                  <Switch id="verified-only" data-testid="verified-only-toggle" checked={verifiedOnly}
                    onCheckedChange={(v) => { setVerifiedOnly(v); if (selected && v && !isVerified(selected)) setSelected(null); }} />
                </div>
              </div>

              {verifiedOnly && (
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 mb-3" data-testid="verified-only-notice">
                  Showing only contacts with a verified (High-confidence) email or a phone number.
                  {hiddenCount > 0 && ` ${hiddenCount} lower-confidence record${hiddenCount > 1 ? "s" : ""} hidden.`}
                </p>
              )}

              <div className="space-y-2">
                {shown.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm" data-testid="dm-verified-empty">
                    No verified contacts in this result set. Turn off “Verified Only” to review lower-confidence records (verify before outreach).
                  </div>
                ) : shown.map((c, i) => {
                  const isSel = selected === c;
                  const verified = isVerified(c);
                  return (
                    <button
                      key={i}
                      data-testid={`dm-result-${i}`}
                      onClick={() => setSelected(c)}
                      className={`w-full text-left rounded-md border p-4 transition-colors ${isSel ? "border-gold ring-1 ring-gold bg-gold/5" : "border-border hover:bg-slate-50"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-navy flex items-center gap-1.5">
                            {c.first_name} {c.last_name}
                            {verified && <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" aria-label="verified" />}
                          </div>
                          <div className="text-sm text-muted-foreground">{c.title || "—"}</div>
                          {c.organization && <div className="text-xs text-slate-500">{c.organization}</div>}
                        </div>
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${CONF_STYLES[c.confidence]}`}>{c.confidence} confidence</span>
                      </div>
                      <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                        <span>{c.email || (c.enriched ? "No email" : "Email — not enriched")}{c.email_predicted && <em className="text-amber-700"> (predicted · unverified)</em>}</span>
                        <span>{c.phone || (c.enriched ? "No phone" : "Phone — not enriched")}</span>
                        <span className="flex items-center gap-1">{c.linkedin_url ? <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-gold-dark inline-flex items-center gap-1 hover:underline"><Linkedin className="h-3 w-3" /> LinkedIn</a> : "No LinkedIn"}</span>
                        <span>Source: {c.source}</span>
                      </div>
                      {!c.enriched && (
                        <p className="mt-2 text-[11px] text-slate-500">Contact details not enriched — Apollo People Search returns partial profiles only. Enrich in Apollo or verify manually before outreach.</p>
                      )}
                      {!verified && (
                        <p className="mt-2 text-[11px] text-amber-700">Lower-confidence / unverified — verify manually before outreach.</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
            );
          })()}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="dm-cancel">Cancel</Button>
          <Button variant="outline" onClick={search} data-testid="dm-search-again">
            <RefreshCw className="h-4 w-4 mr-2" /> Search Again
          </Button>
          {state.status === "ok" && (
            <>
              <Button variant="outline" onClick={handleSaveAdditional} disabled={!selected} data-testid="dm-save-additional">
                Save as Additional Contact
              </Button>
              <Button onClick={handleApply} disabled={!selected} className="bg-navy text-white hover:bg-navy-700" data-testid="dm-apply">
                <Search className="h-4 w-4 mr-2" /> Apply to Lead
              </Button>
            </>
          )}
        </DialogFooter>

        <AlertDialog open={pendingApply} onOpenChange={setPendingApply}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Replace existing contact?</AlertDialogTitle>
              <AlertDialogDescription>
                This lead already has contact information. Do you want to replace it, or save this as an additional contact?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button variant="outline" onClick={() => { setPendingApply(false); handleSaveAdditional(); }} data-testid="dm-keep-additional">
                Save as Additional
              </Button>
              <AlertDialogAction onClick={() => { setPendingApply(false); doApply(selected); }} data-testid="dm-confirm-replace" className="bg-navy hover:bg-navy-700">
                Replace Contact
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
